import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { TESTIDS } from '@/constants/testIds';

const LOGO_URL = '/images/anuruchi-logo.png';

const navLinks = [
  { label: 'Home', path: '/', testId: TESTIDS.NAV_HOME },
  { label: 'About', path: '/about', testId: TESTIDS.NAV_ABOUT },
  { label: 'Products', path: '/products', testId: TESTIDS.NAV_PRODUCTS },
  { label: 'Clients', path: '/clients', testId: TESTIDS.NAV_CLIENTS },
  { label: 'Contact', path: '/contact', testId: TESTIDS.NAV_CONTACT },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-[#1B3A6B] shadow-lg shadow-black/20 py-2'
            : 'bg-[#1B3A6B]/95 py-3'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid={TESTIDS.NAV_LOGO} className="flex-shrink-0">
            <img
              src={LOGO_URL}
              alt="अनुरुची मसाले"
              className="h-12 md:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={link.testId}
                className={`nav-link-underline text-sm font-lato tracking-wider uppercase transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-[#C9922C] active'
                    : 'text-[#F9F3E8] hover:text-[#C9922C]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Enquire Now CTA */}
          <Link
            to="/contact"
            data-testid={TESTIDS.NAV_ENQUIRE_BTN}
            className="hidden md:inline-flex items-center gap-2 bg-[#C9922C] text-[#1A1A1A] font-cinzel text-xs font-semibold tracking-widest uppercase px-6 py-2.5 hover:bg-[#E8A020] transition-colors duration-300"
          >
            Enquire Now
          </Link>

          {/* Mobile Menu Toggle */}
          <button
            data-testid={TESTIDS.MOBILE_MENU_TOGGLE}
            onClick={() => setMobileOpen(true)}
            className="md:hidden text-[#F9F3E8] p-2"
            aria-label="Open menu"
          >
            <Menu size={26} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#1B3A6B] flex flex-col"
          >
            <div className="flex items-center justify-between px-4 py-4">
              <img src={LOGO_URL} alt="Logo" className="h-12" />
              <button
                data-testid={TESTIDS.MOBILE_MENU_CLOSE}
                onClick={() => setMobileOpen(false)}
                className="text-[#F9F3E8] p-2"
                aria-label="Close menu"
              >
                <X size={28} />
              </button>
            </div>
            <div className="flex flex-col items-center justify-center flex-1 gap-6">
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                >
                  <Link
                    to={link.path}
                    data-testid={`mobile-${link.testId}`}
                    className={`font-playfair text-2xl tracking-wide transition-colors ${
                      location.pathname === link.path ? 'text-[#C9922C]' : 'text-[#F9F3E8]'
                    }`}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Link
                  to="/contact"
                  className="btn-gold mt-4"
                >
                  Enquire Now
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
